import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    useScrollTrigger,
    Slide,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';

const HideOnScroll = ({ children }) => {
    const trigger = useScrollTrigger();
    return (
        <Slide appear={false} direction="down" in={!trigger}>
            {children}
        </Slide>
    );
};

const Navbar = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [activeSection, setActiveSection] = useState('home');
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const sections = ['home', 'about', 'services', 'contact'];
            const scrollPosition = window.scrollY + 100;

            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (
                        scrollPosition >= offsetTop &&
                        scrollPosition < offsetTop + offsetHeight
                    ) {
                        setActiveSection(section);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleNavClick = (section) => {
        setActiveSection(section);
        setMobileOpen(false); // Close mobile menu when navigating
        const element = document.getElementById(section);
        if (element) {
            const offset = 70; // Account for fixed navbar height
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        }
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const drawer = (
        <Box
            sx={{
                width: 250,
                height: '100%',
                backgroundColor: '#000000',
                color: 'white',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.tertiary.main}`,
                }}
            >
                <Box
                    component="img"
                    src="/logo.jpg"
                    alt="JOCAST Machining Logo"
                    sx={{
                        height: 40,
                        objectFit: 'contain',
                    }}
                />
                <IconButton
                    onClick={handleDrawerToggle}
                    sx={{
                        color: 'white',
                        '&:hover': {
                            color: theme.palette.tertiary.main,
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <List>
                {[
                    { text: 'Home', section: 'home' },
                    { text: 'About', section: 'about' },
                    { text: 'Services', section: 'services' },
                    { text: 'Contact Us', section: 'contact' },
                ].map((item) => (
                    <ListItem key={item.section} disablePadding>
                        <ListItemButton
                            onClick={() => handleNavClick(item.section)}
                            sx={{
                                color:
                                    activeSection === item.section
                                        ? theme.palette.tertiary.main
                                        : 'white',
                                fontWeight:
                                    activeSection === item.section ? 700 : 400,
                                py: 2,
                                px: 3,
                                '&:hover': {
                                    backgroundColor: 'rgba(184, 134, 11, 0.1)',
                                    color: theme.palette.tertiary.main,
                                },
                            }}
                        >
                            <ListItemText primary={item.text} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    return (
        <>
            <HideOnScroll>
                <AppBar
                    position="fixed"
                    sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                >
                    <Toolbar
                        sx={{
                            justifyContent: 'space-between',
                            py: 1,
                            px: { xs: 2, md: 3 },
                        }}
                    >
                        <Box
                            component="img"
                            src="/logo.jpg"
                            alt="JOCAST Machining Logo"
                            sx={{
                                height: { xs: 40, md: 50 },
                                cursor: 'pointer',
                                objectFit: 'contain',
                            }}
                            onClick={() => handleNavClick('home')}
                        />
                        {isMobile ? (
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="end"
                                onClick={handleDrawerToggle}
                                sx={{
                                    color: 'white',
                                    '&:hover': {
                                        color: theme.palette.tertiary.main,
                                    },
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                        ) : (
                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <Button
                                    color="secondary"
                                    onClick={() => handleNavClick('home')}
                                    sx={{
                                        color:
                                            activeSection === 'home'
                                                ? theme.palette.tertiary.main
                                                : 'white',
                                        fontWeight:
                                            activeSection === 'home' ? 700 : 400,
                                        '&:hover': {
                                            color: theme.palette.tertiary.main,
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    Home
                                </Button>
                                <Button
                                    color="secondary"
                                    onClick={() => handleNavClick('about')}
                                    sx={{
                                        color:
                                            activeSection === 'about'
                                                ? theme.palette.tertiary.main
                                                : 'white',
                                        fontWeight:
                                            activeSection === 'about' ? 700 : 400,
                                        '&:hover': {
                                            color: theme.palette.tertiary.main,
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    About
                                </Button>
                                <Button
                                    color="secondary"
                                    onClick={() => handleNavClick('services')}
                                    sx={{
                                        color:
                                            activeSection === 'services'
                                                ? theme.palette.tertiary.main
                                                : 'white',
                                        fontWeight:
                                            activeSection === 'services'
                                                ? 700
                                                : 400,
                                        '&:hover': {
                                            color: theme.palette.tertiary.main,
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    Services
                                </Button>
                                <Button
                                    color="secondary"
                                    onClick={() => handleNavClick('contact')}
                                    sx={{
                                        color:
                                            activeSection === 'contact'
                                                ? theme.palette.tertiary.main
                                                : 'white',
                                        fontWeight:
                                            activeSection === 'contact'
                                                ? 700
                                                : 400,
                                        '&:hover': {
                                            color: theme.palette.tertiary.main,
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    Contact Us
                                </Button>
                            </Box>
                        )}
                    </Toolbar>
                </AppBar>
            </HideOnScroll>
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: 250,
                    },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
};

export default Navbar;
